import React from 'react';
import scrollIntoView from 'dom-scroll-into-view';
import throttle from 'lodash/throttle';
import moment from 'moment';
import ToggleField from 'components/pure/utils/form/ToggleField';
import SharingFormActions from 'actions/sharing/SharingFormActions';
import SharingActions from 'actions/sharing/SharingActions';
import UserActions from 'actions/UserActions';
import DepartmentActions from 'actions/department/DepartmentActions';
import AuthStore from 'stores/AuthStore';
import FileUploadSharingDialogFormStore from 'stores/sharing/FileUploadSharingDialogFormStore';
import UserStore from 'stores/UserStore';
import FileUploadSharingDialogFormComments from './FileUploadSharingDialogFormComments';
import FileUploadSharingDialogFormContentToShare from './FileUploadSharingDialogFormContentToShare';
import FileUploadSharingDialogFormExternalShares from './FileUploadSharingDialogFormExternalShares';
import FileUploadSharingDialogFormSharingTargets from './FileUploadSharingDialogFormSharingTargets';
import 'styles/lib/forms.styl';
import 'styles/sharing/DirectCallSharingDialog.styl';
import {EMAIL_REGEX, UUID_REGEX} from 'utils/regexValidators';

/**
 * Form used to specify params for sharing a call.
 */
class FileUploadSharingDialogForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // a state that takes on a different value (by setting to a timestamp) when a re-rendering is needed
      rerenderHelper: 0,
      lastChangedField: '',
    };
    this.onDataChanged = this.onDataChanged.bind(this);
    this.onPopoverRef = this.onPopoverRef.bind(this);
    // Each component's scrolling need to be individually throttled so that one does not preempt another
    this.throttledOnPopoverRefTargets = throttle(this.onPopoverRef, 300, {
      trailing: true,
    });
    this.throttledOnPopoverRefComments = throttle(this.onPopoverRef, 300, {
      trailing: true,
    });
    this.throttledOnPopoverRefContents = throttle(this.onPopoverRef, 300, {
      trailing: true,
    });
    this.targetsErrorAnchor = null;
    this.commentsErrorAnchor = null;
    this.contentsErrorAnchor = null;
    this.onShownRef = this.onShownRef.bind(this);
    this.onToggleRequestFeedback = this.onToggleRequestFeedback.bind(this);
  }

  targetsAreSomeExternal() {
    return (
      this.props.sharingTargets &&
      this.props.sharingTargets.length > 0 &&
      this.props.sharingTargets.some(t => EMAIL_REGEX.test(t))
    );
  }

  onDataChanged(dataChanged) {
    this.setState({lastChangedField: dataChanged});
    if (dataChanged === 'targets') {
      const fileUpload = this.props.fileUpload;
      // If a user is sharing her own call, then we want to know more about the internal users she is sharing
      // with, because if at least one of them is a manager or an admin, then we turn on the "request feedback" toggle
      if (
        fileUpload.callDetails.caller &&
        fileUpload.callDetails.caller.id &&
        AuthStore.getState().user.id === fileUpload.callDetails.caller.id
      ) {
        const userIdsOrEmails = FileUploadSharingDialogFormStore.getState().get('sharingTargets');
        const userIds = userIdsOrEmails.filter(data => UUID_REGEX.test(data));
        UserActions.fetchAll.defer({ids: userIds});
      }
    }
    // Note that we can return if the changes are from the external settings component, because in that component
    // there cannot be any errors (about missing data), so we don't need to force a data validation, but I chose
    // to not make the logic too specific, just a few wasted CPU cycles, nbd.
    //
    // Only if the user has submitted once do we want to alert on any missing data
    if (this.props.shareSubmitAttempted) {
      SharingActions.sharingDataChanged(this.props.fileUpload);
    }
  }

  componentDidMount() {
    if (!this.props.boundingDiv) {
      // A hack to force a refresh of the dialog, boundingDiv is null when this form is first rendered,
      // only on a second rendering will it become a <div> node that we can call getBoundingClientRect() on
      SharingActions.sharingDataChanged.defer(null);
      DepartmentActions.fetchUserDepartment.defer();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.targetsErrorAnchor = null;
    this.commentsErrorAnchor = null;
    this.contentsErrorAnchor = null;
  }

  /**
   * This function takes a ref from a popover and decides if the popover is out-of-bounds, if so, it
   * tells the containing component of that popover to use a different anchor.
   */
  onPopoverRef(label, ref) {
    if (label === 'targets') {
      this.onPopoverRefForComponent(
        ref,
        this.targetsErrorAnchor,
        value => {
          this.targetsErrorAnchor = value;
        },
        true
      );
    } else if (label === 'comments') {
      this.onPopoverRefForComponent(
        ref,
        this.commentsErrorAnchor,
        value => {
          this.commentsErrorAnchor = value;
        },
        true
      );
    } else if (label === 'contents') {
      this.onPopoverRefForComponent(
        ref,
        this.contentsErrorAnchor,
        value => {
          this.contentsErrorAnchor = value;
        },
        false
      );
    }
  }

  onPopoverRefForComponent(ref, componentAnchor, componentSetter, isTop) {
    // XXX, may need to deal with multiple "Please scroll up" popovers on top of each other
    if (ref) {
      // tolerance is arbitrary, allow the Popover to go slightly higher than the scrolling pane
      const tolerance = 30;
      let changed = false;
      if (
        (isTop &&
          ref.getBoundingClientRect().top <
            this.props.boundingDiv.getBoundingClientRect().top - tolerance) ||
        (!isTop &&
          ref.getBoundingClientRect().bottom >
            this.props.boundingDiv.getBoundingClientRect().bottom + tolerance)
      ) {
        if (!componentAnchor) {
          componentSetter(isTop ? this.props.topErrorAnchor : this.props.bottomErrorAnchor);
          changed = true;
        }
      } else {
        // If the errorAnchor is the same as the passed-in anchor, then it is just the popover at the errorAnchor
        // telling us that it is above the line, well, we know that, and we are not going to clear the errorAnchor
        // because of that
        if (componentAnchor) {
          componentSetter(null);
          changed = true;
        }
      }
      if (changed) {
        // force a re-rendering
        this.setState({rerenderHelper: moment().valueOf()});
      }
    }
  }

  onShownRef(field, ref) {
    const boundingDiv = this.props.boundingDiv;
    // We use startsWith because for external settings onDataChanged is called with a more fine-grained name, like
    // "settings.password", while here the field is just "settings"
    if (
      ref &&
      boundingDiv &&
      this.state.lastChangedField &&
      this.state.lastChangedField.startsWith(field)
    ) {
      const container = this.props.scrollDiv();
      if (
        ref &&
        container &&
        ref.getBoundingClientRect().bottom > boundingDiv.getBoundingClientRect().bottom
      ) {
        scrollIntoView(ref, container, {
          alignWithBottom: true,
          offsetBottom: 20,
        });
      }
      this.setState({lastChangedField: ''});
    }
  }

  onToggleRequestFeedback(event, toggled) {
    SharingFormActions.updateRequestFeedback(toggled);
  }

  isRequestFeedbackEligible() {
    const fileUpload = this.props.fileUpload;
    if (fileUpload.callDetails.caller && fileUpload.callDetails.caller.id) {
      // the user has to be the caller (i.e. her call)
      if (AuthStore.getState().user.id === fileUpload.callDetails.caller.id) {
        const userIdsOrEmails = FileUploadSharingDialogFormStore.getState().get('sharingTargets');
        const userIds = userIdsOrEmails.filter(data => UUID_REGEX.test(data));
        const managerOrAdmin = userIds.find(userId => {
          const user = UserStore.getState().users.get(userId);
          return user && (user.isManager() || user.isAdmin());
        });
        if (managerOrAdmin) {
          return true;
        }
      }
    }
    return false;
  }

  render() {
    // get error strings for each label
    const shareDataError = this.props.shareDataError;
    const sharingTargetsError = shareDataError.get('sharingTargets');
    const contentToShareError = shareDataError.get('contentToShare');
    const highlightsError = shareDataError.get('tags');
    const commentsError = shareDataError.get('comments');

    return (
      <div className="share-form-container">
        <div className="left">&nbsp;</div>
        <form
          className="share-form-horizontal"
          autoComplete="off"
          onSubmit={e => {
            e.preventDefault();
          }}>
          <label className="overflow share-with-area">
            <div className="text-input-label">Share with</div>
            <FileUploadSharingDialogFormSharingTargets
              className="text-input"
              name="sharingTargets"
              addTeams={true}
              scrollDiv={this.props.scrollDiv}
              sharingValues={this.props.sharingValues}
              error={sharingTargetsError}
              onDataChanged={this.onDataChanged}
              onPopoverRef={this.throttledOnPopoverRefTargets}
              errorAnchor={this.targetsErrorAnchor}
              department={this.props.department}
            />
          </label>

          <FileUploadSharingDialogFormComments
            {...this.props}
            onDataChanged={this.onDataChanged}
            onPopoverRef={this.throttledOnPopoverRefComments}
            errorAnchor={this.commentsErrorAnchor}
            error={commentsError}
          />

          {this.isRequestFeedbackEligible() && (
            <ToggleField
              label="I'm requesting feedback"
              toggled={this.props.requestFeedback}
              onToggle={this.onToggleRequestFeedback}
            />
          )}

          {this.props.highlights && this.props.highlights.size > 0 && (
            <FileUploadSharingDialogFormContentToShare
              {...this.props}
              highlights={this.props.highlights.toJS()}
              contentToShareError={contentToShareError}
              highlightsError={highlightsError}
              onDataChanged={this.onDataChanged}
              onPopoverRef={this.throttledOnPopoverRefContents}
              errorAnchor={this.contentsErrorAnchor}
              onShownRef={this.onShownRef}
              scrollDiv={this.props.scrollDiv}
            />
          )}

          {this.targetsAreSomeExternal() && (
            <FileUploadSharingDialogFormExternalShares
              {...this.props}
              onDataChanged={this.onDataChanged}
              onShownRef={this.onShownRef}
            />
          )}
        </form>
      </div>
    );
  }
}

export default FileUploadSharingDialogForm;
